import {
  type CreateUpdateOrderDTO,
  CustomerType,
  type NexiRetrievePaymentResponse,
  type OrderDTO,
  type PaymentProviderNexiOptions
} from 'ecosystem'
import { convertMinorUnitToValue, roundNumber } from 'shared-utils'
import lodashSumBy from 'lodash/sumBy'
import { createError } from '../../../../utils/fetching'
import { generateNexiAddresses } from '../utils'
import { type CompleteStorefrontOrderProps } from './types'
import {
  generateBasePayload,
  generatePayloadFromDefaultDetails,
  generatePayloadFromIngrid,
  generatePayloadFromNshift,
  generatePayloadFromPostNord
} from './utils'

const generateBasePayloadFromNexi = (
  oldOrder: OrderDTO,
  nexiResponse: NexiRetrievePaymentResponse,
  _nexiOptions?: PaymentProviderNexiOptions
): CreateUpdateOrderDTO => {
  const { payment } = nexiResponse
  const nexiItems = payment.charges?.[0].orderItems ?? []

  const {
    billingAddress,
    shippingAddress,
    customerInfo: { email, firstName, lastName }
  } = generateNexiAddresses(nexiResponse)

  const orderDiscount = nexiItems.reduce((sum, item) => {
    return item.reference === 'discount' ? sum + Math.abs(item.grossTotalAmount) : sum
  }, 0)

  const shippingCost = nexiItems.reduce((sum, item) => {
    return item.reference === 'delivery' ? sum + item.grossTotalAmount : sum
  }, 0)

  const orderAmount = convertMinorUnitToValue(payment.orderDetails.amount)
  const orderTaxAmount = convertMinorUnitToValue(lodashSumBy(nexiItems, 'taxAmount'))

  return {
    ...generateBasePayload(oldOrder),
    billingAddress,
    shippingAddress,
    currency: payment.orderDetails.currency,
    customer: {
      email,
      firstName,
      lastName,
      type: CustomerType.CONSUMER
    },
    orderAmount,
    orderTaxAmount,
    orderDiscount: roundNumber(convertMinorUnitToValue(orderDiscount)),
    shippingCost: convertMinorUnitToValue(shippingCost),
    paymentId: payment.paymentId,
    paymentProvider: 'NEXI'
  }
}

export const nexiPaymentProvider = ({
  order: oldOrder,
  paymentProviderInfo,
  shippingProviderInfo
}: CompleteStorefrontOrderProps): CreateUpdateOrderDTO => {
  if (!paymentProviderInfo.providerResponse?.nexiResponse) {
    throw createError('Not found NEXI order')
  }
  const shippingProviderName = shippingProviderInfo?.providerName ?? 'DEFAULT'

  let payload = generateBasePayloadFromNexi(
    oldOrder,
    paymentProviderInfo.providerResponse?.nexiResponse,
    paymentProviderInfo.providerOptions?.nexi
  )

  switch (shippingProviderName) {
    case 'DEFAULT':
      // Carry the FE-supplied delivery selection forward from the
      // persisted order — see `PaymentNexiDefault.tsx` / `sveaPaymentProvider`.
      payload = generatePayloadFromDefaultDetails(payload, oldOrder)
      break
    case 'POSTNORD':
      payload = generatePayloadFromPostNord(payload, shippingProviderInfo?.postnordResponse)
      break
    case 'INGRID':
      payload = generatePayloadFromIngrid(payload, shippingProviderInfo?.ingridResponse)
      break
    case 'NSHIFT':
      payload = generatePayloadFromNshift(payload, shippingProviderInfo?.nshiftResponse)
  }

  return payload
}
